import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useReducer,
} from 'react';
import { Form, Select, Table, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { parseABWorkFile, getJobCodeOptions  } from './ABWorkImportHelpers';
import OnTraccrCheckbox from '../common/inputs/OnTraccrCheckbox';

const summaryTypes = new Set(['97', '95']);

export default function ABWorkImportParsing({
  uploadFile,
  importRows,
  setImportRows,
  setParentMap,
  setRecordMap,
  setChildMap,
  setHasParsingError,
  hasParsingError,
  isAcceptRejectFile,
  setIsAcceptRejectFile,
  jobCode,
  setJobCode,
}) {
  const [fileDetails, setFileDetails] = useState(null);
  const [shouldUpdate, forceUpdate] = useReducer((x) => x + 1, 0);
  const [isRoutineWork, setIsRoutineWork] = useState(false);
  const jobCodes = useSelector((state) => state.abWorkReporting.jobCodes);
  const {
    routine,
    provisional,
  } = getJobCodeOptions(jobCodes);

  const {
    recordTypeMap,
    parentMap,
    recordMap,
    isAcceptRejectFile: isAcceptRejectFileFromParse,
    error,
    childMap,
  } = useMemo(() => parseABWorkFile(fileDetails), [fileDetails]);

  if (isAcceptRejectFileFromParse !== isAcceptRejectFile) {
    setIsAcceptRejectFile(isAcceptRejectFileFromParse);
  }

  if (hasParsingError !== !!error) {
    setHasParsingError(!!error);
  }

  useEffect(() => {
    setRecordMap(recordMap);
  }, [recordMap, setRecordMap]);

  useEffect(() => {
    setParentMap(parentMap);
  }, [parentMap, setParentMap]);

  useEffect(() => {
    setChildMap(childMap);
  }, [childMap, setChildMap]);

  useEffect(() => {
    const loadFileDetails = async () => {
      const fileURL = URL.createObjectURL(uploadFile);
      const response = await fetch(fileURL);
      const text = await response.text();
      URL.revokeObjectURL(fileURL);

      if (text) {
        const rows = text.split('\n');
        setFileDetails(rows);
      } else {
        setFileDetails(null);
      }
    };

    if (uploadFile) {
      loadFileDetails();
    }

    return () => {
      setFileDetails(null);
    };
  }, [uploadFile]);

  // Use Effect to default select all import rows when the data is loaded in
  useEffect(() => {
    const newImportRows = {};
    Object.keys(recordTypeMap).forEach((recordType) => {
      newImportRows[recordType] = recordTypeMap[recordType].rows.map((row) => row.id);
    });
    setImportRows(newImportRows);

    return () => {
      setImportRows({});
    };
  }, [recordTypeMap, setImportRows]);

  const onChange = useCallback((recordType) => (selectedRowKeys) => {
    if (recordType === '20') {
      const previousImportRows = importRows[recordType];
      const selectedRowKeySet = new Set(selectedRowKeys);
      const previousRowKeySet = new Set(previousImportRows);
      const removedImportRows = previousImportRows.filter((row) => !selectedRowKeySet.has(row));
      const addedImportRows = selectedRowKeys.filter((row) => !previousRowKeySet.has(row));
      const updates = {
        record22ToAdd: new Set(),
        record22ToRemove: new Set(),
        record25ToAdd: new Set(),
        record25ToRemove: new Set(),
      };

      removedImportRows.forEach((row) => {
        if (parentMap[row]) {
          parentMap[row].forEach((parentRow) => {
            if (parentRow.recordType === '22') {
              updates.record22ToRemove.add(parentRow.id);
              parentMap[parentRow.id]?.forEach((childRow) => {
                updates.record25ToRemove.add(childRow.id);
              });
            }
          });
        }
      });

      addedImportRows.forEach((row) => {
        if (parentMap[row]) {
          parentMap[row].forEach((parentRow) => {
            if (parentRow.recordType === '22') {
              updates.record22ToAdd.add(parentRow.id);
              parentMap[parentRow.id]?.forEach((childRow) => {
                updates.record25ToAdd.add(childRow.id);
              });
            }
          });
        }
      });

      // We need to deselect any children rows if the parent row is deselected and vice versa
      setImportRows((prevState) => {
        const newState = { ...prevState };
        newState[recordType] = selectedRowKeys;

        if (updates.record22ToRemove.size > 0) {
          newState['22'] = newState['22'].filter((row) => !updates.record22ToRemove.has(row));
        }
        if (updates.record25ToRemove.size > 0) {
          newState['25'] = newState['25'].filter((row) => !updates.record25ToRemove.has(row));
        }

        newState['22'].push(...Array.from(updates.record22ToAdd));
        newState['25'].push(...Array.from(updates.record25ToAdd));
        return newState;
      });

      // Need to setTimeout to force a re-render
      setTimeout(() => {
        forceUpdate();
      });
    } else if (recordType === '25') {
      const allRows = recordTypeMap['25']?.rows || [];
      const selectedSet = new Set(selectedRowKeys);
      const selectedOriginalIds = new Set();
      allRows.forEach((row) => {
        if (selectedSet.has(row.id)) {
          selectedOriginalIds.add(row.originalId || row.id);
        }
      });
      const newSelected = allRows
        .filter((row) => selectedOriginalIds.has(row.originalId || row.id))
        .map((row) => row.id);
      setImportRows((prevState) => ({
        ...prevState,
        25: newSelected,
      }));
    } else {
      setImportRows((prevState) => ({
        ...prevState,
        [recordType]: selectedRowKeys,
      }));
    }
  }, [importRows, setImportRows, recordTypeMap]);

  const onSelectAll = useCallback((recordType) => (checked) => {
    const newIds = checked ? recordTypeMap[recordType].rows.map((row) => row.id) : [];
    setImportRows((prevState) => ({
      ...prevState,
      [recordType]: newIds,
    }));
  }, [importRows, setImportRows, recordTypeMap]);

  if (error) {
    return <div className="ab-work-reporting-error">{error}</div>;
  }

  return (
    <div>
      { !isAcceptRejectFile && (
        <>
          <Form.Item label="Job Code" required>
            <Select
              options={isRoutineWork ? routine : provisional}
              value={jobCode}
              onChange={setJobCode}
            />
          </Form.Item>
          <OnTraccrCheckbox
            label="Routine Work?"
            value={isRoutineWork}
            onChange={(checked) => {
              setIsRoutineWork(checked);
              setJobCode();
            }}
            style={{ marginBottom: 20 }}
          />
        </>
      )}
      {Object.keys(recordTypeMap).map((recordType) => {
        let { rows } = recordTypeMap[recordType];
        if (recordType === '25') {
          const seen = new Set();
          rows = rows.filter((row) => {
            if (!row.originalId || row.originalId === row.id) return true;
            if (seen.has(row.originalId)) return false;
            seen.add(row.originalId);
            return true;
          });
        }

        return (
          <React.Fragment key={`${recordType}${shouldUpdate}`}>
            <Typography.Title level={3}>Record Type {recordType}</Typography.Title>
            <Table
              dataSource={rows}
              columns={recordTypeMap[recordType].columns}
              size="small"
              pagination={false}
              style={{ paddingBottom: 20 }}
              onHeaderRow={() => ({
                className: 'ontraccr-table-header',
              })}
              rowSelection={summaryTypes.has(recordType) ? null : {
                selectedRowKeys: importRows[recordType],
                onChange: onChange(recordType),
                onSelectAll: onSelectAll(recordType),
                getCheckboxProps: () => ({
                  disabled: false,
                  className: 'ontraccr-checkbox',
                }),
              }}
              rowKey="id"
            />
          </React.Fragment>
        );
      })}
    </div>
  );
}

ABWorkImportParsing.propTypes = {
  uploadFile: PropTypes.instanceOf(File).isRequired,
  importRows: PropTypes.shape({}).isRequired,
  setImportRows: PropTypes.func.isRequired,
  setParentMap: PropTypes.func.isRequired,
  setRecordMap: PropTypes.func.isRequired,
  setChildMap: PropTypes.func.isRequired,
  isAcceptRejectFile: PropTypes.bool.isRequired,
  setIsAcceptRejectFile: PropTypes.func.isRequired,
  hasParsingError: PropTypes.bool.isRequired,
  setHasParsingError: PropTypes.func.isRequired,
  jobCode: PropTypes.string.isRequired,
  setJobCode: PropTypes.func.isRequired,
};
